import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { ConfirmationPopupComponent } from '../../components/confirmation-popup/confirmation-popup.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { ButtonComponent } from "../../components/button/button.component";
import { HeaderComponent } from "../../components/header/header.component";
import { TextInputComponent } from "../../components/text-input/text-input.component";
import { PluralPipe } from '../../pipes/plural.pipe';
import { DateInputComponent } from "../../components/date-input/date-input.component";
import { BulkUploadComponent } from '../../components/bulk-upload/bulk-upload.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { SelectInputComponent } from "../../components/select-input/select-input.component";
import { AcademicYearService } from '../../services/academic-year.service';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { ValidationService } from '../../services/validation.service';
import { DropdownService } from '../../services/dropdown.service';
import { DropdownUploadButtonComponent } from '../../components/dropdown-upload-button/dropdown-upload-button.component';
@Component({
  selector: 'app-academic-year-list-page',
  imports: [HeaderComponent, ButtonComponent, CommonModule, NgIf, DatePipe, ConfirmationPopupComponent, ModalComponent, TextInputComponent, ReactiveFormsModule, PluralPipe, SelectInputComponent, AgGridListComponent, BulkUploadComponent, SearchInputComponent, DateInputComponent, DropdownUploadButtonComponent],
  templateUrl: './academic-year-list-page.component.html',
  styleUrl: './academic-year-list-page.component.css'
})
export class AcademicYearListPageComponent {
  @ViewChild('confirmation_delete_modal') confirmation_delete_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('confirmation_disabled_modal') confirmation_disabled_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;
  @ViewChild('bulk_modal') bulk_modal: any
  @ViewChild('view_modal') view_modal: ModalComponent | undefined;

  page_name = 'Academic Year';
  form: FormGroup;
  params: any = {};
  selected_ids: any = {}
  items: any = [];
  academicYears: string[] = [];
  academicYearRanges: { startDate: Date, endDate: Date, id?: number }[] = [];
  currentDate: string = new Date(Date.now()).toISOString().split('T')[0];
  isSubmitting = false;
  show_menu = false;


  columns: any = [
    { headerName: 'Sr.No', field: 'SrNo', flex: 1 },
    { field: 'name', headerName: 'Academic Year', filter: true, flex: 1, },
    {
      field: 'start_date',
      headerName: 'Start Date',
      filter: true,
      flex: 1,
      valueFormatter: (params: any) => {
        if (params.value) {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-GB');
        }
        return '';
      }
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      filter: true,
      flex: 1,
      valueFormatter: (params: any) => {
        if (params.value) {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-GB');
        }
        return '';
      }
    },
    { field: 'is_active', headerName: 'Status', filter: true, flex: 1, valueGetter: (params: any) => params.data?.is_active, cellRenderer: (params: any) => { return params.value ? 'Enable' : 'Disable'; } },

    { headerName: "Actions", sortable: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellRendererParams: { actions: this.actions.bind(this) }, editable: false, pinned: 'right', width: 210, minWidth: 210, maxWidth: 210 }
  ];

  bulk_columns = [
    { column_name: 'Academic Year Name', type: 'text', return_as: 'name', sample_value: '2025-2026', regex: /^\d{4}-\d{4}$/, is_unique: true },
    { column_name: 'Start Date', type: 'text', return_as: 'start_date', sample_value: '2025-01-01' },
    { column_name: 'End Date', type: 'text', return_as: 'end_date', sample_value: '2025-12-31' },
  ];

  // Custom validator for academic year format
  academicYearValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) return { required: true };

    if (value.length < 9) {
      return { minLength: true };
    }

    const pattern = /^\d{4}-\d{4}$/;
    if (!pattern.test(value)) {
      return { invalidFormat: true };
    }

    // Validate that the difference is exactly 1 year
    const years = value.split('-');
    const startYear = parseInt(years[0], 10);
    const endYear = parseInt(years[1], 10);

    if (endYear - startYear !== 1) {
      return { invalidYearDifference: true };
    }



    return null;
  }

  // Validator for start date - must be within academic year first year
  startDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return { required: true };

    const academicYear = control.parent?.get('name')?.value;
    const endDateValue = control.parent?.get('end_date')?.value;

    if (!academicYear || !/^\d{4}-\d{4}$/.test(academicYear)) return null;

    const startDate = new Date(value);
    const years = academicYear.split('-');
    const startYear = parseInt(years[0], 10);

    if (startDate.getFullYear() !== startYear) {
      return { startDateNotInRange: true };
    }

    if (endDateValue) {
      const endDate = new Date(endDateValue);
      if (startDate > endDate) {
        return { startDateAfterEndDate: true };
      }
    }

    return null;
  }

  // Validator for end date - must be > start date and within 1 year from start date
  endDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return { required: true };

    const startDateValue = control.parent?.get('start_date')?.value;

    if (!startDateValue) return null;

    const endDate = new Date(value);
    const startDate = new Date(startDateValue);

    if (endDate < startDate) {
      return { endDateBeforeStartDate: true };
    }

    return null;
  }

  actions(_row: any) {
    let actions: any = []
    actions.push({ text: _row.is_active ? 'Disable' : 'Enable', shortcut: 'u', color: 'gray', icon: _row.is_active ? 'bx bx-block' : 'bx bx-check-circle', function: this.disable.bind(this), permissions: this.gs.user_role_data?.evaluation_management_academic_year_disable })
    actions.push({ text: 'View', shortcut: 'u', color: 'sky', icon: 'bx bx-eye', function: this.open_view_modal.bind(this), permissions: this.gs.user_role_data?.evaluation_management_academic_year_view })
    actions.push({ text: 'Update', shortcut: 'u', color: 'sky', icon: 'bx bx-edit', disabled: _row.is_active === false, function: this.open_form_modal.bind(this), permissions: this.gs.user_role_data?.evaluation_management_academic_year_update })
    actions.push({ text: 'Delete', shortcut: 'd', color: 'red', icon: 'bx bx-trash', function: this.open_delete_modal.bind(this), permissions: this.gs.user_role_data?.evaluation_management_academic_year_delete })
    return actions
  }
  constructor(public gs: GlobalService, public route: Router, public fb: FormBuilder, public ays: AcademicYearService, public ar: ActivatedRoute, public vs: ValidationService, public ds: DropdownService) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/^[0-9-]+$/), Validators.pattern(/^\d{4}-\d{4}$/), this.academicYearValidator.bind(this)]],
      start_date: ['', [Validators.required, this.startDateValidator.bind(this)]],
      end_date: ['', [Validators.required, this.endDateValidator.bind(this)]],
    });

    // Re-validate dates when academic year changes
    this.form?.get('name')?.valueChanges.subscribe(() => {
      this.form?.get('start_date')?.updateValueAndValidity();
      this.form?.get('end_date')?.updateValueAndValidity();
    });

    // Re-validate end date when start date changes
    this.form?.get('start_date')?.valueChanges.subscribe(() => {
      this.form?.get('end_date')?.updateValueAndValidity();
    });
  }
  async ngOnInit() {
    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params }
      await this.get_list(this.params)
    })
  }
  async get_list(filter?: any) {
    try {
      const res = await this.ays.get_list(filter);
      this.academicYears = res.data.map((item: any) => item.name);

      // Populate array of academic year date ranges (for overlap checks)
      this.academicYearRanges = res.data.map((item: any) => ({
        startDate: new Date(item.start_date),
        endDate: new Date(item.end_date),
        id: item.id // to skip checking overlap with self during edit
      }));
      const page = filter?.page || 1;
      const pagesize = filter?.page_size || 10;
      const startindex = (page - 1) * pagesize;

      res.data = res.data.map((item: any, index: number) => ({
        ...item,
        SrNo: startindex + index + 1
      }));

      this.items = res;
    } catch (error: any) {
      console.error("Error fetching Booking list:", error);
    }
  }
  async open_delete_modal(item: any, _index: any) {
    this.selected_ids = item;
    this.confirmation_delete_modal?.open();
  }
  open_view_modal(item: any) {
    console.log(item);
    this.selected_ids = item || {};
    this.view_modal?.open();
  }
  async disable(item: any, _index: any) {
    this.selected_ids = item;
    this.confirmation_disabled_modal?.open();
  }
  async update_country(item: any) {
    try {
      let form_value = this.form.value;
      let data = item == true ? false : true;
      await this.ays.update(this.selected_ids.id, { is_active: data });
      this.gs.toastr_shows_function('Status has been successfully updated', 'Success', 'success');
      this.confirmation_delete_modal?.close()
      this.ds.invalidate_dropdown_cache('academic-year');
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async delete() {
    try {
      let res = await this.ays.delete(this.selected_ids.id);
      if (res != undefined) {
        this.gs.toastr_shows_function(res?.message, 'Success', 'success');
      }
      this.selected_ids = {};
      this.confirmation_delete_modal?.close()
      this.ds.invalidate_dropdown_cache('academic-year');
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async bulk_delete(ids: any[]) {
    for (let id of ids) {
      await this.ays.delete(id);
    }
    this.gs.toastr_shows_function('Records deleted successfully', 'Success', 'success');
    this.ds.invalidate_dropdown_cache('academic-year');
    await this.get_list(this.params);
  }
  async open_form_modal(item?: any) {
    this.selected_ids = item || {};
    if (this.selected_ids && this.selected_ids.id) {
      let response = await this.ays.get(item.id);
      this.selected_ids = this.gs.addHierarchyIds({ ...response.data });
      this.form.patchValue({ ...this.selected_ids, });
    } else {
      this.selected_ids = {};
      this.form.reset();
    }
    this.form_modal?.open();
  }
  close_form_modal(): void {
    this.form_modal?.close();
  }

  async submit() {
    try {
      if (this.isSubmitting) {
        return;
      }
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      this.isSubmitting = true;
      let form_value = this.form.value;
      form_value.is_active = true;
      if (this.selected_ids && this.selected_ids.id) {
        await this.ays.update(this.selected_ids.id, form_value);
        this.gs.toastr_shows_function('Changes updated successfully', 'Success', 'success');
      } else {
        let create_response = await this.ays.add(form_value);
        this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      }
      this.ds.invalidate_dropdown_cache('academic-year');
      await this.get_list(this.params);
      this.close_form_modal();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  async bulk_upload(data: any, spinner: any) {
    try {
      console.log(data);

      const updatedData = data.map((item: any) => ({
        name: item.name,
        start_date: this.toYYYYMMDD(item.start_date),
        end_date: this.toYYYYMMDD(item.end_date),
        is_active: true
      }));
      await this.gs.bulk_form_submission(updatedData, this.ays, this.params, this.get_list.bind(this), this.bulk_modal, spinner)
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message, 'Error', 'error');
    }
  }

  toYYYYMMDD(value: any): string {
    if (!value) return '';

    // Date object
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }

    // dd-mm-yyyy OR dd/mm/yyyy
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(value)) {
      const [d, m, y] = value.split(/[-/]/);
      return `${y}-${m}-${d}`;
    }

    // yyyy-mm-dd OR yyyy/mm/dd
    if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(value)) {
      return value.replace(/\//g, '-');
    }

    // ❌ NEVER trust JS Date for custom strings
    return '';
  }

  // Get max date for end date (one year after start date minus 1 day)
  get maxEndDate(): string | null {
    const startDateValue = this.form.get('start_date')?.value;
    if (!startDateValue) return null;

    const startDate = new Date(startDateValue);
    const maxDate = new Date(startDate);
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    maxDate.setDate(maxDate.getDate() - 1); // Subtract 1 day

    return maxDate.toISOString().split('T')[0];
  }

  // Get min date for start date based on academic year (1st of January of the first year)
  get minStartDate(): string | null {
    const academicYear = this.form.get('name')?.value;
    if (!academicYear || !/^\d{4}-\d{4}$/.test(academicYear)) return null;

    const years = academicYear.split('-');
    const startYear = parseInt(years[0], 10);
    const minDate = new Date(`${startYear}-01-01`);

    return minDate.toISOString().split('T')[0];
  }
  // Get default date for start date picker (1st of January of the first year)
  get defaultStartDate(): string | null {
    const academicYear = this.form.get('name')?.value;
    if (!academicYear || !/^\d{4}-\d{4}$/.test(academicYear)) return null;

    const years = academicYear.split('-');
    const startYear = parseInt(years[0], 10);
    const defaultDate = new Date(`${startYear}-01-01`);

    return defaultDate.toISOString().split('T')[0];
  }
  // Get max date for start date (31st of December of the first year)
  get maxStartDate(): string | null {
    const academicYear = this.form.get('name')?.value;
    if (!academicYear || !/^\d{4}-\d{4}$/.test(academicYear)) return null;

    const years = academicYear.split('-');
    const startYear = parseInt(years[0], 10);
    const maxDate = new Date(`${startYear}-12-31`);

    return maxDate.toISOString().split('T')[0];
  }

}
